import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { FAIconName, useToastMessage } from '@equip.health/ui';
import {
  DAY_OF_WEEK_DISABLED_FREQUENCIES,
  DEFAULT_SECTION_FORM_STATE,
} from '~/lib/constants/groupClasses.constants';
import { useGetProviderTimezonesEnumQuery } from '~/store/services/enumerations';
import {
  useCreateGroupSessionMutation,
  useEditGroupSessionMutation,
  useGetFrequencyOptionsQuery,
  useGetGroupSessionDetailsQuery,
  useGetSessionOptionsQuery,
} from '~/store/services/groupClass';
import { GROUP_SECTION_MAX_ATTENDEES_LIMIT } from '~/lib/util/classes/groupClasses.util';
import {
  formatDateTimeInTimezone,
  getEndDateMinDate,
} from '~/lib/util/date.util';
import { getDefaultTimezoneIANA } from '~/lib/util/timezone.util';
import {
  getMaximumAttendeesErrorMessage,
  hasFormChanges,
} from '~/lib/util/form.util';
import {
  isFormValid,
  normalizeSectionDetailsToFormState,
  getDayOfWeekFieldUpdates,
  validateStartDateTime,
  validateDayOfWeekMatch,
  calculateStartDateUpdate,
  calculateTimezoneUpdate,
} from '~/lib/util/groups.utils';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import {
  mergeStartDateTimeValues,
  applyAutoPopulatedStartDateTimeUpdates,
  getFrequencyChangeUpdates,
  calculateStartTimeUpdate,
  prepareFormValue,
  shouldClearDayOfWeekError,
  prepareSubmitRequestData,
} from './sectionForm.utils';

type AutoPopulatedStartDateTime = {
  startDate: string;
  startTime: string;
  sessionDateTime: Date | null;
};

type StartDateTimeUpdate = Partial<
  Pick<GroupSectionFormData, 'startDate' | 'startTime' | 'sessionDateTime'>
>;

const EMPTY_AUTO_POPULATED_START_DATETIME: AutoPopulatedStartDateTime = {
  startDate: '',
  startTime: '',
  sessionDateTime: null,
};

interface UseCreateEditSectionProps {
  groupStatusCode: GroupStatusType;
  groupId: string;
  groupSectionId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const useCreateEditSection = ({
  groupStatusCode,
  groupId,
  groupSectionId,
  isOpen,
  onClose,
  onSuccess,
}: UseCreateEditSectionProps) => {
  const isEditMode = !!groupSectionId;
  const isLiveGroup = groupStatusCode === 'LIVE';
  const isFieldDisabledForLiveGroup = isEditMode && isLiveGroup;
  const draftEditMode = isEditMode && !isLiveGroup;

  const { trackEvent } = useAnalytics();
  const { createToast } = useToastMessage();

  const [createGroupSession, { isLoading: isCreating }] =
    useCreateGroupSessionMutation();
  const [editGroupSession, { isLoading: isEditing }] =
    useEditGroupSessionMutation();

  const [formState, setFormState] = useState<GroupSectionFormData>(
    DEFAULT_SECTION_FORM_STATE,
  );
  const [initialFormState, setInitialFormState] =
    useState<GroupSectionFormData>(DEFAULT_SECTION_FORM_STATE);
  const [userEditedStartTime, setUserEditedStartTime] = useState<string>('');
  const latestStartTimeRef = useRef<string>('');
  const [autoPopulatedStartDateTime, setAutoPopulatedStartDateTime] =
    useState<AutoPopulatedStartDateTime>(EMPTY_AUTO_POPULATED_START_DATETIME);
  const [isWarningCloseModalOpen, setIsWarningCloseModalOpen] = useState(false);

  const [hasUserEditedStartDate, setHasUserEditedStartDate] = useState(false);
  const [hasUserEditedMaxAttendees, setHasUserEditedMaxAttendees] =
    useState(false);

  const [startDateTimeError, setStartDateTimeError] = useState<string>('');
  const [dayOfWeekMismatchError, setDayOfWeekMismatchError] =
    useState<string>('');

  const { data: sectionData, isFetching: isFetchingSection } =
    useGetGroupSessionDetailsQuery(
      {
        groupId,
        sectionId: groupSectionId || '',
      },
      { skip: !groupSectionId },
    );

  const { data: timezonesResponse } = useGetProviderTimezonesEnumQuery();
  const { data: frequencyOptionsResponse } = useGetFrequencyOptionsQuery();
  const { data: sessionOptionsResponse } = useGetSessionOptionsQuery();

  const frequencyOptions = useMemo(
    () =>
      frequencyOptionsResponse?.map(({ frequency, code }) => ({
        label: frequency,
        value: code,
      })) || [],
    [frequencyOptionsResponse],
  );

  const sessionOptions = useMemo(
    () =>
      sessionOptionsResponse?.map(({ displayName, code }) => ({
        label: displayName,
        value: code,
      })) || [],
    [sessionOptionsResponse],
  );

  const timeZones = useMemo(
    () =>
      timezonesResponse?.map(({ code, id }) => ({
        label: code,
        node: id,
      })) || [],
    [timezonesResponse],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!groupSectionId) {
      const defaultState = {
        ...DEFAULT_SECTION_FORM_STATE,
        timezone: getDefaultTimezoneIANA(timezonesResponse),
      };
      setFormState(defaultState);
      setInitialFormState(defaultState);
      resetEditedStartTimeValue();
      setAutoPopulatedStartDateTime(EMPTY_AUTO_POPULATED_START_DATETIME);
      setHasUserEditedStartDate(false);
      setHasUserEditedMaxAttendees(false);
      setStartDateTimeError('');
      setDayOfWeekMismatchError('');
      return;
    }

    if (!sectionData || isFetchingSection) {
      return;
    }

    const normalizedData = normalizeSectionDetailsToFormState(
      sectionData,
      getDefaultTimezoneIANA(timezonesResponse),
    );

    setFormState(normalizedData);
    setInitialFormState(normalizedData);
    resetEditedStartTimeValue();
    setAutoPopulatedStartDateTime({
      startDate: normalizedData.startDate,
      startTime: normalizedData.startTime,
      sessionDateTime: normalizedData.sessionDateTime,
    });
    setHasUserEditedStartDate(normalizedData.showDate || false);
    setHasUserEditedMaxAttendees(normalizedData.showMaximumAttendees || false);
    setStartDateTimeError('');
    setDayOfWeekMismatchError('');
  }, [
    groupSectionId,
    sectionData,
    isFetchingSection,
    isOpen,
    timezonesResponse,
  ]);

  const shouldDisableDayOfWeek = DAY_OF_WEEK_DISABLED_FREQUENCIES.has(
    formState.frequency,
  );

  const mergedStartDateTime = useMemo(
    () =>
      mergeStartDateTimeValues(
        formState,
        autoPopulatedStartDateTime,
        userEditedStartTime,
      ),
    [
      formState.startDate,
      formState.startTime,
      formState.sessionDateTime,
      autoPopulatedStartDateTime.startDate,
      autoPopulatedStartDateTime.startTime,
      autoPopulatedStartDateTime.sessionDateTime,
      userEditedStartTime,
    ],
  );

  useEffect(() => {
    if (!mergedStartDateTime.startDate || !mergedStartDateTime.startTime) {
      setStartDateTimeError('');
      setDayOfWeekMismatchError('');
      return;
    }

    const dateTimeError = validateStartDateTime(
      mergedStartDateTime.startDate,
      mergedStartDateTime.startTime,
      formState.timezone,
      isEditMode,
    );
    setStartDateTimeError(dateTimeError);

    const shouldValidateDayOfWeek =
      hasUserEditedStartDate || (isEditMode && formState.showDate);

    if (
      shouldValidateDayOfWeek &&
      !shouldDisableDayOfWeek &&
      formState.dayOfWeek
    ) {
      const dayOfWeekError = validateDayOfWeekMatch(
        mergedStartDateTime.startDate,
        formState.dayOfWeek,
        formState.timezone,
        shouldDisableDayOfWeek,
      );
      setDayOfWeekMismatchError(dayOfWeekError);
    } else if (!shouldValidateDayOfWeek || shouldDisableDayOfWeek) {
      setDayOfWeekMismatchError('');
    }
  }, [
    mergedStartDateTime,
    formState.dayOfWeek,
    formState.timezone,
    formState.showDate,
    hasUserEditedStartDate,
    shouldDisableDayOfWeek,
    isEditMode,
  ]);

  const getMergedStartDateTime = (
    state: GroupSectionFormData,
    autoPopulated: AutoPopulatedStartDateTime,
  ) => {
    return mergeStartDateTimeValues(
      state,
      autoPopulated,
      latestStartTimeRef.current || userEditedStartTime,
    );
  };

  const setEditedStartTimeValue = (nextStartTime: string) => {
    setUserEditedStartTime(nextStartTime);
    latestStartTimeRef.current = nextStartTime;
  };

  const resetEditedStartTimeValue = () => {
    setUserEditedStartTime('');
    latestStartTimeRef.current = '';
  };

  const updateAutoPopulatedStartDateTime = (updates: StartDateTimeUpdate) => {
    setAutoPopulatedStartDateTime((prevAuto) =>
      applyAutoPopulatedStartDateTimeUpdates(prevAuto, updates),
    );
  };

  const applyFrequencyFieldUpdate = ({
    field,
    value,
    prev,
    updates,
  }: {
    field: keyof GroupSectionFormData;
    value: GroupSectionFormData[keyof GroupSectionFormData];
    prev: GroupSectionFormData;
    updates: Partial<GroupSectionFormData>;
  }) => {
    if (
      field !== 'frequency' ||
      typeof value !== 'string' ||
      isFieldDisabledForLiveGroup
    ) {
      return;
    }

    const frequencyUpdates = getFrequencyChangeUpdates({
      frequency: value,
      prev,
      autoPopulated: autoPopulatedStartDateTime,
      latestEditedStartTime: latestStartTimeRef.current || userEditedStartTime,
      DAY_OF_WEEK_DISABLED_FREQUENCIES,
      getDayOfWeekFieldUpdates,
    });

    updates.dayOfWeek = frequencyUpdates.dayOfWeek;

    if (frequencyUpdates.autoPopulatedUpdates) {
      updateAutoPopulatedStartDateTime(frequencyUpdates.autoPopulatedUpdates);
    }
  };

  const applyDayOfWeekFieldUpdate = ({
    field,
    value,
    prev,
    updates,
  }: {
    field: keyof GroupSectionFormData;
    value: GroupSectionFormData[keyof GroupSectionFormData];
    prev: GroupSectionFormData;
    updates: Partial<GroupSectionFormData>;
  }) => {
    if (field !== 'dayOfWeek' || typeof value !== 'string' || !value) {
      return;
    }

    const dayOfWeekUpdates = getDayOfWeekFieldUpdates(value, prev);

    updateAutoPopulatedStartDateTime({
      startDate: dayOfWeekUpdates.startDate,
      startTime: dayOfWeekUpdates.startTime,
      sessionDateTime: dayOfWeekUpdates.sessionDateTime,
    });

    if (
      draftEditMode &&
      !hasUserEditedStartDate &&
      dayOfWeekUpdates.startDate
    ) {
      updates.startDate = '';
      updates.sessionDateTime = dayOfWeekUpdates.sessionDateTime;
    }
  };

  const applyStartDateFieldUpdate = ({
    field,
    value,
    prev,
    updates,
  }: {
    field: keyof GroupSectionFormData;
    value: GroupSectionFormData[keyof GroupSectionFormData];
    prev: GroupSectionFormData;
    updates: Partial<GroupSectionFormData>;
  }) => {
    if (field !== 'startDate' || typeof value !== 'string' || !value) {
      return;
    }

    const mergedStartDateTime = getMergedStartDateTime(
      prev,
      autoPopulatedStartDateTime,
    );

    if (!mergedStartDateTime.startTime) {
      return;
    }

    const sessionDateTime = calculateStartDateUpdate(
      value,
      mergedStartDateTime.startTime,
      prev.timezone,
    );
    if (sessionDateTime) {
      updates.sessionDateTime = sessionDateTime;
    }
  };

  const applyTimezoneFieldUpdate = ({
    field,
    value,
    prev,
    updates,
  }: {
    field: keyof GroupSectionFormData;
    value: GroupSectionFormData[keyof GroupSectionFormData];
    prev: GroupSectionFormData;
    updates: Partial<GroupSectionFormData>;
  }) => {
    if (field !== 'timezone' || typeof value !== 'string') {
      return;
    }

    const mergedStartDateTime = getMergedStartDateTime(
      prev,
      autoPopulatedStartDateTime,
    );

    if (!mergedStartDateTime.sessionDateTime) {
      return;
    }

    const timezoneUpdate = calculateTimezoneUpdate(
      mergedStartDateTime.sessionDateTime,
      value,
    );

    if (prev.startDate) {
      updates.startDate = timezoneUpdate.startDate;
    }
    if (prev.startTime) {
      updates.startTime = timezoneUpdate.startTime;
    }
    updates.sessionDateTime = timezoneUpdate.sessionDateTime;

    if (userEditedStartTime) {
      setEditedStartTimeValue(timezoneUpdate.startTime);
    }

    setAutoPopulatedStartDateTime((prevAuto) =>
      applyAutoPopulatedStartDateTimeUpdates(prevAuto, {
        startDate: prevAuto.startDate ? timezoneUpdate.startDate : undefined,
        startTime: prevAuto.startTime ? timezoneUpdate.startTime : undefined,
        sessionDateTime: timezoneUpdate.sessionDateTime,
      }),
    );
  };

  const updateFormField = <K extends keyof GroupSectionFormData>(
    field: K,
    value: GroupSectionFormData[K],
  ) => {
    if (shouldClearDayOfWeekError(field)) {
      setDayOfWeekMismatchError('');
    }

    setFormState((prev) => {
      const updates: Partial<GroupSectionFormData> = {
        [field]: prepareFormValue(value),
      };

      applyFrequencyFieldUpdate({ field, value, prev, updates });
      applyDayOfWeekFieldUpdate({ field, value, prev, updates });
      applyStartDateFieldUpdate({ field, value, prev, updates });
      applyTimezoneFieldUpdate({ field, value, prev, updates });

      return { ...prev, ...updates };
    });
  };

  const handleStartTimeChange = (date: Date) => {
    const localDt = DateTime.fromJSDate(date);
    const timeStr = localDt.toFormat('HH:mm:ss');
    const mergedStartDateTime = getMergedStartDateTime(
      formState,
      autoPopulatedStartDateTime,
    );

    const timeUpdate = calculateStartTimeUpdate(
      timeStr,
      mergedStartDateTime.startDate,
      formState.timezone,
    );

    setFormState((prev) => ({
      ...prev,
      ...timeUpdate,
    }));
    setEditedStartTimeValue(timeStr);
  };

  const hasChanges = hasFormChanges(
    { ...formState, showMaximumAttendees: hasUserEditedMaxAttendees },
    initialFormState,
    ['sessionDateTime'],
  );

  const effectiveEndDate = useMemo(
    () => (formState.frequency === 'DOES_NOT_REPEAT' ? '' : formState.endDate),
    [formState.frequency, formState.endDate],
  );

  const trackCancelEvent = () => {
    trackEvent(
      isEditMode
        ? GroupSectionEvents.editSectionCancelButtonClicked
        : GroupSectionEvents.addSectionCancelButtonClicked,
      {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      },
    );
  };

  const handleClose = () => {
    if (hasChanges) {
      setIsWarningCloseModalOpen(true);
    } else {
      trackCancelEvent();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    trackCancelEvent();
    setIsWarningCloseModalOpen(false);
    setFormState(initialFormState);
    resetEditedStartTimeValue();
    onClose();
  };

  const handleSubmit = () => {
    trackEvent(
      isEditMode
        ? GroupSectionEvents.editSectionSaveChangesButtonClicked
        : GroupSectionEvents.addSectionSaveChangesButtonClicked,
      {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
        Hosts: formState.hosts.map((host) => host.value),
        Frequency: formState.frequency,
        'Day of week': formState.dayOfWeek,
        'Start time': mergedStartDateTime.startTime,
        Timezone: formState.timezone,
        'Duration (minutes)': formState.duration,
        Length: formState.length,
        'Start date': mergedStartDateTime.startDate,
        'End date': effectiveEndDate,
        'Max attendees': formState.maximumAttendees,
        'Must attend all': formState.mustAttendAll,
        'Is webinar': formState.isWebinar,
      },
    );

    const requestData: CreateSectionRequest = prepareSubmitRequestData({
      groupId,
      isEditMode,
      groupSectionId,
      formState,
      mergedStartDateTime,
      effectiveEndDate,
      hasUserEditedStartDate,
      hasUserEditedMaxAttendees,
      formatDateTimeInTimezone,
    });

    const submitSection = isEditMode ? editGroupSession : createGroupSession;

    submitSection(requestData)
      .unwrap()
      .then(() => {
        createToast({
          id: `group-section-${isEditMode ? 'edit' : 'create'}-success`,
          text: `Section ${isEditMode ? 'updated' : 'created'} successfully`,
          variant: 'success',
          icon: FAIconName.checkRegular,
        });
        onSuccess?.();
        onClose();
      })
      .catch(() => {
        createToast({
          id: `group-section-${isEditMode ? 'edit' : 'create'}-error`,
          text: `Failed to ${isEditMode ? 'update' : 'create'} section`,
          variant: 'critical',
          icon: FAIconName.exclamationTriangleRegular,
        });
      });
  };

  const formValid = isFormValid(
    {
      ...formState,
      endDate: effectiveEndDate,
      startDate: mergedStartDateTime.startDate,
      startTime: mergedStartDateTime.startTime,
      sessionDateTime: mergedStartDateTime.sessionDateTime,
    },
    shouldDisableDayOfWeek,
    isLiveGroup,
    isEditMode,
  );

  const shouldDisableSubmit = isEditMode
    ? !hasChanges ||
      !formValid ||
      !!startDateTimeError ||
      !!dayOfWeekMismatchError
    : !formValid || !!startDateTimeError || !!dayOfWeekMismatchError;

  const maximumAttendeesError = getMaximumAttendeesErrorMessage(
    formState.maximumAttendees,
    GROUP_SECTION_MAX_ATTENDEES_LIMIT,
  );

  return {
    formState,
    mergedStartDateTime,
    isWarningCloseModalOpen,
    setIsWarningCloseModalOpen,
    hasUserEditedStartDate,
    setHasUserEditedStartDate,
    hasUserEditedMaxAttendees,
    setHasUserEditedMaxAttendees,
    isEditMode,
    isFieldDisabledForLiveGroup,
    shouldDisableDayOfWeek,
    hasChanges,
    effectiveEndDate,
    formValid,
    shouldDisableSubmit,
    frequencyOptions,
    sessionOptions,
    timeZones,
    isFetchingSection,
    isCreating,
    isEditing,
    startDateTimeError,
    dayOfWeekMismatchError,
    maximumAttendeesError,
    updateFormField,
    handleStartTimeChange,
    handleClose,
    handleConfirmClose,
    handleSubmit,
    getEndDateMinDate,
  };
};
